import React, { useEffect, useState } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import Icon, { IconType } from '../Icon/Icon';
import StoreFront from '../Store/StoreFront';
import OrderHistory from '../Store/OrderHistory';
import { IState, ReduxMap } from '../../redux/redux';
import { store } from '../../redux/oldStore';
import { IParticipantData, IRegFeeRequest } from './ShopTypes';
import { FormatMoney } from '../../utils/Tools';
import { toast } from 'react-toastify';
import { ServerResponse } from '../../utils/Server';

interface ShopPageProps extends IState {}

// Component for Participants tab
const ParticipantsStub = ({ participantData, onSwitchToStore }: { participantData: IParticipantData | null; onSwitchToStore: () => void }) => {
  const [selectedSchool, setSelectedSchool] = useState<string>('');
  const [hidePaidInFull, setHidePaidInFull] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<IRegFeeRequest[]>([]);

  const addToCart = ()=>{
    if(selectedItems.length ===0){
      toast.info('No items selected to add to cart.');
      return;
    }

    store.server.postApi<ServerResponse<boolean>>('../Store/AddParticipantFeesToCart', selectedItems).then((x) => {
      if (x.Success) {
        toast.success('Selected participant fees added to cart.');
        onSwitchToStore();
      } else {
        toast.error('Error adding participant fees to cart: ' + x.Message);
      }

    });
  };

  if (!participantData) {
    return (
      <div className="alert alert-info">
        <h4>
          <Icon type={IconType.users} /> Participants Tab
        </h4>
        <p>Loading participant data...</p>
      </div>
    );
  }

  if (!participantData.listDetails || participantData.listDetails.length === 0) {
    return (
      <div className="alert alert-warning">
        <h4>
          <Icon type={IconType.users} /> Participants Tab
        </h4>
        <p>No participant data available.</p>
      </div>
    );
  }

  // Get unique schools from the participant data
  const uniqueSchools = [...new Set(participantData.listDetails.map((detail) => detail.School).filter(Boolean))].sort();

  // Filter participants based on selected school and payment status
  let filteredParticipants = selectedSchool ? participantData.listDetails.filter((detail) => detail.School === selectedSchool) : participantData.listDetails;
  
  if (hidePaidInFull) {
    filteredParticipants = filteredParticipants.filter((detail) => !detail.FeeInfo.PaidInFull);
  }

  return (
    <div className="participants-container">
      <div className="flex-between">
        <div>
        <h4>
          <Icon type={IconType.users} /> Participants
        </h4>
        {filteredParticipants.length} of {participantData.listDetails.length}
        </div>

        <div className="form-horizontal row">
          {/* School Filter Dropdown */}
          <div className="form-group">
            <label htmlFor="school-filter" className="control-label col-sm-4">
              School:
            </label>
            <div className="col-sm-8">
              <select id="school-filter" className="form-control" value={selectedSchool} onChange={(e) => setSelectedSchool(e.target.value)}>
                <option value="">All Schools</option>
                {uniqueSchools.map((school, index) => (
                  <option key={index} value={school}>
                    {school}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Hide Paid in Full Toggle */}
          <div className="form-group">
            <div className="col-sm-offset-4 col-sm-8">
                <label className='control-label'>
                  <input
                    type="checkbox"
                    checked={hidePaidInFull}
                    onChange={(e) => setHidePaidInFull(e.target.checked)}
                  />
                  Hide Paid In Full {!hidePaidInFull && <>({filteredParticipants.filter((detail) => detail.FeeInfo.PaidInFull).length})</>}
                </label>
            </div>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="thead-dark">
            <tr>
              <th></th>
              <th>Participant</th>
              <th>School</th>
              <th>Fee</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredParticipants.map((detail, index) => {
              let paid = detail.FeeInfo.PaidInFull;
              return (              
              <tr key={`${detail.ProjectId}-${detail.PersonId}-${index}`}>
                <td className='middle'>
                  {!paid && <>
                  
                    <input type='checkbox' className='add-fee-checkbox'
                      onChange={(x=>{
                        let checked = x.target.checked;
                        if(checked){
                          setSelectedItems([...selectedItems,{
                            personId: detail.PersonId,
                            projectId: detail.ProjectId,
                            cost: detail.FeeInfo.AmountRequired,
                            description: detail.OrderDescription
                          }]);
                        } else{
                          setSelectedItems(selectedItems.filter(item=>!(item.personId === detail.PersonId && item.projectId === detail.ProjectId)));
                        } 


                      })}
                    />
                  </>}
                </td>
                <td>
                  <strong>{detail.Label}</strong>
                  <div>{detail.SubLabel}</div>
                  <small>{detail.SubSubLabel}</small>
                </td>
                <td>{detail.School}</td>
                <td>
                  <strong className="no-break">{FormatMoney(detail.FeeInfo.AmountRequired)}</strong>
                </td>
                <td>
                  <div>
                    {detail.FeeInfo.PaidInFull ? (
                      <span className="label label-success">
                        <Icon type={IconType.check} /> Paid in Full
                      </span>
                    ) : (
                      <span className="label label-warning">
                        <Icon type={IconType.exclamation} /> Payment Required
                      </span>
                    )}
                    {detail.Invoices && detail.Invoices.length > 0 && (
                      <div style={{ marginTop: '4px' }}>
                        <span className="label label-info">
                          <Icon type={IconType.fileInvoice} /> {detail.Invoices.length === 1 ? 'Has Invoice' : 'Has Invoices'}
                        </span>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>

      {selectedItems.length >0 && (
        <div className="flex-end" style={{marginBottom: '10px'}}>
          <button className="btn btn-primary" onClick={addToCart}>
            <Icon type={IconType.shoppingCart} /> Add Selected Fees to Cart ({selectedItems.length})
          </button>
          <button className="btn btn-secondary" onClick={() => {
            setSelectedItems([]);
            let checkboxes = document.getElementsByClassName('add-fee-checkbox') as HTMLCollectionOf<HTMLInputElement>;
            for(let i=0;i<checkboxes.length;i++){
              checkboxes[i].checked = false;  
            }}}>
            Clear Selection
          </button>
        </div>
      )}

      {filteredParticipants.length === 0 && selectedSchool && (
        <div className="alert alert-info">
          <p>No participants found for "{selectedSchool}".</p>
        </div>
      )}
    </div>
  );
};

const ShopPage = (props: ShopPageProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [canViewParticipants, setCanViewParticipants] = useState<boolean>(false);
  const [participantData, setParticipantData] = useState<IParticipantData | null>(null);

  useEffect(() => {
    // Determine if the user has permission to view participants
    if (props.User) {
      // Example permission check, adjust as needed
      setCanViewParticipants(props.User.SecurityLevel >= 1);
    }
  }, [props.User]);

  const loadParticipantsData = () => {
    store.server.getApi2<IParticipantData>('../Store/ParticipantData').then((x) => {
      if (x.Success) {
        setParticipantData(x.Value);
      } else {
        alert('Error loading participant data: ' + x.Message);
      }
    });
  };

  return (
    <div className="shop-page">
      <h2>Shop</h2>

      <Tabs selectedIndex={activeTabIndex} onSelect={(index) => setActiveTabIndex(index)}>
        <TabList>
          <Tab onSelect={() => {}}>
            <Icon type={IconType.shoppingCart} /> Store
          </Tab>
          {canViewParticipants && (
            <Tab
              onClick={() => {
                loadParticipantsData();
              }}>
              <Icon type={IconType.users} /> Participants
            </Tab>
          )}
          {props.User?.signedIn && (
            <Tab>
              <Icon type={IconType.fileInvoice} /> Order History
            </Tab>
          )}
        </TabList>

        <TabPanel>
          <StoreFront notRegistrationStore={true} />
        </TabPanel>

        {canViewParticipants && (
          <TabPanel>
            <ParticipantsStub participantData={participantData} onSwitchToStore={() => setActiveTabIndex(0)} />
          </TabPanel>
        )}

        {props.User?.signedIn && (
          <TabPanel>
            <OrderHistory showWhenEmpty={true} />
          </TabPanel>
        )}
      </Tabs>
    </div>
  );
};

export default ReduxMap(ShopPage);
